import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-smart-toaster";

const formatDate = (value) => (value ? dayjs(value).format("MMM D, YYYY") : "");

const remainingText = (value) => {
  const minutes = dayjs(value).diff(dayjs(), "minute");
  if (minutes <= 0) return "";
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  return days > 0 ? `${days}d ${hours}h` : `${hours}h ${minutes % 60}m`;
};

/*
 * Display values for a guest vehicle's parking pass, from `guest_pass` on the v1/vehicles/list|grid rows
 * (status NONE, AWAITING_PAYMENT, PAYMENT_FAILED, PAYMENT_EXPIRED, ACTIVE or PASS_ENDED).
 * tone: active | awaiting | expired | none — each view maps it to its own colors and icons.
 */
export const guestPassDisplay = (guestPass) => {
  const status = guestPass?.status || "NONE";
  const passName = guestPass?.label || "";
  const price = guestPass?.amount ? Number(guestPass.amount) : null;
  const shortName = passName.replace(/ pass$/i, "") || "Pass";

  const display = {
    status,
    tone: "none",
    badge: "No pass",
    subLabel: "",
    statusText: "No pass",
    passName: passName || "-",
    price,
    validFrom: null,
    wasValid: null,
    expires: null,
    remaining: null,
    alertTone: "info",
    alertText: "No parking pass yet. Send the guest a payment link.",
    // The guest pays on the pay page; an active pass needs nothing
    canSend: status !== "ACTIVE",
    sendLabel: status === "PASS_ENDED" ? "Send renewal link" : "Send payment link",
    revokeLabel: status === "ACTIVE" ? "Revoke access" : "Cancel pass",
  };

  if (status === "ACTIVE") {
    const remaining = remainingText(guestPass.expires_at);
    const endsSoon = dayjs(guestPass.expires_at).diff(dayjs(), "hour") < 24;
    return {
      ...display,
      tone: "active",
      badge: `Active · ${shortName}`,
      subLabel: `Expires ${formatDate(guestPass.expires_at)}`,
      statusText: "Paid & Active",
      validFrom: formatDate(guestPass.starts_at),
      expires: formatDate(guestPass.expires_at),
      remaining,
      alertTone: endsSoon ? "warning" : "",
      alertText: endsSoon ? "Pass expires in less than 24 hours. Send a renewal link once it ends." : "",
    };
  }

  if (status === "PASS_ENDED") {
    return {
      ...display,
      tone: "expired",
      badge: `Expired · ${shortName}`,
      subLabel: `Expired ${formatDate(guestPass.expires_at)}`,
      statusText: "Expired",
      wasValid: `${formatDate(guestPass.starts_at)} – ${formatDate(guestPass.expires_at)}`,
      alertTone: "error",
      alertText: `Access restricted. ${passName || "Pass"} expired ${formatDate(guestPass.expires_at)}.`,
    };
  }

  if (status === "AWAITING_PAYMENT") {
    return {
      ...display,
      tone: "awaiting",
      badge: "Awaiting payment",
      subLabel: `Link expires ${formatDate(guestPass.link_expires_at)}`,
      statusText: "Awaiting payment",
      alertTone: "warning",
      alertText: `Payment link sent. The guest has not paid yet; the link expires ${formatDate(guestPass.link_expires_at)}.`,
    };
  }

  if (status === "PAYMENT_FAILED") {
    return {
      ...display,
      tone: "expired",
      badge: "Payment failed",
      subLabel: `Link expires ${formatDate(guestPass.link_expires_at)}`,
      statusText: "Payment failed",
      alertTone: "error",
      alertText: `The guest's payment didn't go through. They can retry from the same link until ${formatDate(guestPass.link_expires_at)}, or send a new payment link.`,
    };
  }

  if (status === "PAYMENT_EXPIRED") {
    return {
      ...display,
      tone: "expired",
      badge: "Payment link expired",
      subLabel: guestPass.link_expires_at ? `Expired ${formatDate(guestPass.link_expires_at)}` : "",
      statusText: "Payment link expired",
      alertTone: "error",
      alertText: "The payment link expired before the guest paid. Send a new payment link.",
    };
  }

  return display;
};

// "Send payment link" on the Vehicles page: texts the guest the pay page, then reloads the list (onListChange)
export const useSendPaymentLink = (onListChange) => {
  const { getAccessTokenSilently } = useAuth0();
  const [sendingId, setSendingId] = useState(0);

  const sendPaymentLink = async (vehicleId) => {
    setSendingId(vehicleId);
    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      const response = await axios.post(process.env.REACT_APP_TENANT_API_URL + "v1/vehicles/" + vehicleId + "/send-payment-link", {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response?.data?.message || "Payment link sent to the guest.");
      if (onListChange) onListChange();
    } catch (error) {
      console.error("[Vehicles] Failed to send the payment link", error);
      toast.error(error?.response?.data?.message || "Unable to send the payment link. Please try again.");
      // A 502 still created/refreshed the pass, so show the new status
      if (error?.response?.status === 502 && onListChange) onListChange();
    } finally {
      setSendingId(0);
    }
  };

  return [sendingId, sendPaymentLink];
};
